import React, { useState, useRef, useEffect } from "react";
import logo from "../../../../assets/chatbot.png";
import logo1 from "../../../../assets/Image (3).jpg";
import {
  MessageCircle,
  Send,
  X,
  Edit2,
  Trash2,
  Bot,
  User,
  Download,
  CheckSquare,
  Square,
  Check,
} from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../../../api-client/AxiosClient";
import appConfig from "../../../../config/appConfig";

const FalconAIChatbot = ({ fileId }) => {
  // --- Core state (single in-memory chat session) ---
  const [userMappingFileId, setUserMappingFileId] = useState(null);
  const [messages, setMessages] = useState([]); // [{ id, text, sender, timestamp, hasDownload, originalQuery, data }]
  const [message, setMessage] = useState("");
  const [selectedRows, setSelectedRows] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [approvingMessageId, setApprovingMessageId] = useState(null);
  const [approvalInProgress, setApprovalInProgress] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  // const [chatTitle, setChatTitle] = useState("Falcon AI Chat");
  const [currentFileName, setCurrentFileName] = useState(() => {
     return sessionStorage.getItem("selectedFileName") || "Mapping Assistant";
   });

  // --- refs & helpers ---
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

// Get current user email
  const getCurrentUserEmail = () => {
    try {
      const accountKey = JSON.parse(sessionStorage.getItem("msal.account.keys"))?.[0];
      if (accountKey) {
        const accountData = JSON.parse(sessionStorage.getItem(accountKey));
        return accountData?.username || null;
      }
      return null;
    } catch (error) {
      //console.error("Error getting current user email:", error);
      return null;
    }
  };

  // Fetch and match user ID
  const fetchCurrentUserId = async () => {
    try {
      const currentUserEmail = getCurrentUserEmail();

      if (!currentUserEmail) {
        //console.warn("No current user email found");
        return null;
      }

      //console.log("Fetching user ID for:", currentUserEmail);

      const response = await axiosClient.get("/api/User/all");

      if (!response || !response.data) {
        //console.warn("No user data received");
        return null;
      }

      const allUsers = Array.isArray(response.data) ? response.data : [];
      const normalizedCurrentEmail = currentUserEmail.toLowerCase().trim();

      let matchedUser = allUsers.find(
        (user) => user.email && user.email.toLowerCase().trim() === normalizedCurrentEmail
      );

      if (!matchedUser) {
        matchedUser = allUsers.find((user) => {
          if (!user.email) return false;
          const normalizedUserEmail = user.email.toLowerCase().trim();
          return (
            normalizedUserEmail.includes(normalizedCurrentEmail) ||
            normalizedCurrentEmail.includes(normalizedUserEmail)
          );
        });
      }

      if (matchedUser) {
        const userId = matchedUser.userId || matchedUser.id || null;
        //console.log("Matched user ID:", userId);
        return userId;
      }

      //console.warn("No matching user found");
      return null;
    } catch (error) {
      //console.error("Error fetching user ID:", error);
      return null;
    }
  };

 

  // Fetch user ID on component mount
  useEffect(() => {
    const loadUserId = async () => {
      const userId = await fetchCurrentUserId();
      setCurrentUserId(userId);
    };
    loadUserId();
  }, []);


//  FIX: Update file ID and reset chat when fileId prop changes
  useEffect(() => {
    if (fileId) {
      setUserMappingFileId(fileId);
      
      // Get the current file name from session storage
      const storedFileName = sessionStorage.getItem("selectedFileName");
      const fileName = storedFileName || "Mapping Assistant";
      setCurrentFileName(fileName);
      
      // Reset messages with new welcome message
      const welcome = {
        id: Date.now(),
        sender: "ai",
        text: `👋 Hello! I am your **${fileName}** assistant. I can help you with:  
- SAP S/4HANA → AX mapping queries`,
        timestamp: new Date(),
        hasDownload: false,
      };
      setMessages([welcome]);
      
      // Clear any selected rows
      setSelectedRows({});
      
      // Clear message input
      setMessage("");
    }
  }, [fileId]);

  //  FIX: Listen for file changes in session storage (cross-component sync)
  useEffect(() => {
    const handleStorageChange = () => {
      const storedFileId = sessionStorage.getItem("selectedFileId");
      const storedFileName = sessionStorage.getItem("selectedFileName");
      
      if (storedFileId && parseInt(storedFileId) !== userMappingFileId) {
        setUserMappingFileId(parseInt(storedFileId));
        setCurrentFileName(storedFileName || "Mapping Assistant");
        
        // Reset chat with new welcome message
        const welcome = {
          id: Date.now(),
          sender: "ai",
          text: `👋 Hello! I am your **${storedFileName || "Mapping Assistant"}** assistant. I can help you with:  
- SAP S/4HANA → AX mapping queries`,
          timestamp: new Date(),
          hasDownload: false,
        };
        setMessages([welcome]);
        setSelectedRows({});
        setMessage("");
      }
    };

    // Listen for custom storage events
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("customStorageChange", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("customStorageChange", handleStorageChange);
    };
  }, [userMappingFileId]);

  // Initial load - only if no fileId prop provided
  useEffect(() => {
    if (!fileId) {
      const storedFileId = sessionStorage.getItem("selectedFileId");
      const storedFileName = sessionStorage.getItem("selectedFileName");
      
      if (storedFileId) {
        setUserMappingFileId(parseInt(storedFileId));
        setCurrentFileName(storedFileName || "Mapping Assistant");
      }
    }
  }, [fileId]);

  // Initial welcome message - only on first mount if no messages
  useEffect(() => {
    if (messages.length === 0) {
      
      const welcome = {
        id: Date.now(),
        sender: "ai",
        text: `👋 Hello! I am your **${currentFileName}** assistant. I can help you with:  
- SAP S/4HANA → AX mapping queries`,
        timestamp: new Date(),
        hasDownload: false,
      };
      setMessages([welcome]);
    }
  }, []);

  // ------------- Selection helpers (checkboxes for rows) -------------
  const handleRowSelect = (messageId, rowIndex) => {
    const key = `${messageId}-${rowIndex}`;
    setSelectedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSelectAll = (messageId, totalRows) => {
    const allSelected = Array.from({ length: totalRows }).every(
      (_, i) => selectedRows[`${messageId}-${i}`]
    );

    const newSelection = { ...selectedRows };
    Array.from({ length: totalRows }).forEach((_, i) => {
      newSelection[`${messageId}-${i}`] = !allSelected;
    });
    setSelectedRows(newSelection);
  };

  const getSelectedRowsForMessage = (messageId, totalRows) => {
    return Array.from({ length: totalRows }).filter(
      (_, i) => selectedRows[`${messageId}-${i}`]
    ).length;
  };



  const handleApproveSelected = async (messageId, selectedData) => {

    if(approvalInProgress){
      return;
    }

    try {
      setApprovingMessageId(messageId);
      setApprovalInProgress(true);

      console.log("Approve clicked for message:", messageId);
      console.log("Selected rows data:", selectedData);

      const updateBulkMapperDetailsRequests = selectedData.map((row) => ({
        targetTableName: row.SAPTableName || row["SAP Table Name"] || "",
        targetFieldName: row.SAPColumnName || row["SAP Column Name"] || "",
        sourceTableName: row.AXTableName || row["AX Table Name"] || "",
        sourceFieldName: row.AXColumnName || row["AX Column Name"] || "",
      }));

      const payload = {
        updateBulkMapperDetailsRequests,
        userMappingFileId: userMappingFileId ,
      };

      console.log("Sending payload to API:", payload);

      const response = await axiosClient.post(
        "/api/Mapper/UpdateBulkMapperFile",
        payload
      );

      console.log("Update response status:", response.status);



      toast.success("✅ Selected mappings approved and saved successfully!");
     
      setSelectedRows((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (key.startsWith(`${messageId}-`)) {
            delete updated[key];
          }
        });
        return updated;
      });
    } catch (error) {
      console.error("Approval Error:", error);
      toast.error(`❌ Failed to approve mappings: ${error.message}`);
      setApprovingMessageId(null);
    } finally {
      setApprovingMessageId(null);
 
      setApprovalInProgress(false);
    }
  };

  // ------------- Agent API integration -------------
  const callAgentAPI = async (query) => {
    try {
      // Retrieve mapping file ID from session storage
      const mappingFileId = sessionStorage.getItem("selectedFileId");

      if (!mappingFileId) {
        return {
          success: false,
          error:
            "Mapping file ID not found. Please select a mapping file first.",
          hasDownloadableData: false,
        };
      }

      const response = await fetch(`${appConfig.AGENT_BASE}/ObjectDriven`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: query,
          user_id :currentUserId,
          thread_id:0, 
          mapping_file_id: mappingFileId,
          download_format: "",
          filename: "",
        }),
      });

     if (!response.ok) {
         const errorData = await response.json().catch(() => null);
         if (errorData) {
          // Extract the error message from the response
          const errorMessage = errorData.message || errorData.detail || `HTTP error! status: ${response.status}`;
          return {
            success: false,
            error: errorMessage,
            errorDetails: errorData,
            hasDownloadableData: false,
          };
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data,
        hasDownloadableData: true,
      };
    } catch (error) {
      console.error("API Error:", error);
      return {
        success: false,
        error: error.message,
        hasDownloadableData: false,
      };
    }
  };

  // ------------- File generation & download fallback -------------
  const generateFileFromDisplayedData = (messageData, format, filename) => {
    try {
      let tableData = null;

      if (Array.isArray(messageData)) {
        tableData = messageData;
      } else if (messageData && typeof messageData === "object") {
        const dataKeys = Object.keys(messageData);
        const commonArrayKeys = [
          "results",
          "sap_columns",
          "sap_tables",
          "data",
        ];
        for (const key of commonArrayKeys) {
          if (
            messageData[key] &&
            Array.isArray(messageData[key]) &&
            messageData[key].length > 0
          ) {
            tableData = messageData[key];
            break;
          }
        }
        if (!tableData) {
          for (const key of dataKeys) {
            if (
              Array.isArray(messageData[key]) &&
              messageData[key].length > 0
            ) {
              tableData = messageData[key];
              break;
            }
          }
        }
      }

      if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
        throw new Error(
          "No valid data to export - no arrays found in the data"
        );
      }

      if (format === "csv") {
        let csvContent;
        if (typeof tableData[0] === "object" && tableData[0] !== null) {
          const allKeys = [
            ...new Set(tableData.flatMap((item) => Object.keys(item))),
          ];
          csvContent = [
            allKeys.join(","),
            ...tableData.map((item) =>
              allKeys
                .map((header) => {
                  const value = item[header] || "";
                  return `"${String(value).replace(/"/g, '""')}"`;
                })
                .join(",")
            ),
          ].join("\n");
        } else {
          csvContent = [
            "Index,Value",
            ...tableData.map(
              (item, index) =>
                `${index + 1},"${String(item).replace(/"/g, '""')}"`
            ),
          ].join("\n");
        }

        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        return { success: true };
      } else {
        throw new Error("Only CSV format is supported in fallback mode");
      }
    } catch (error) {
      console.error("generateFileFromDisplayedData error:", error);
      return { success: false, error: error.message };
    }
  };

  const downloadResponse = async (query, format = "csv") => {
    try {
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const filename = `mapping_result_${timestamp}`;

      // Retrieve mapping file ID from session storage
      const mappingFileId = sessionStorage.getItem("selectedFileId");

      if (!mappingFileId) {
        throw new Error("Mapping file ID not found in session storage");
      }

      const response = await fetch(`${appConfig.AGENT_BASE}/ObjectDriven`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: query,
          mapping_file_id: mappingFileId,
          download_format: format,
          filename: filename,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Download Error Response:", errorText);
        throw new Error(`Download failed! status: ${response.status}`);
      }

      const contentType = response.headers.get("content-type");

      if (format === "csv") {
        if (contentType && contentType.includes("application/json")) {
          const jsonData = await response.json();
          let tableData = null;
          if (Array.isArray(jsonData)) {
            tableData = jsonData;
          } else if (jsonData.results && Array.isArray(jsonData.results)) {
            tableData = jsonData.results;
          } else if (
            jsonData.sap_columns &&
            Array.isArray(jsonData.sap_columns)
          ) {
            tableData = jsonData.sap_columns;
          } else {
            const arrayKeys = Object.keys(jsonData).filter(
              (key) => Array.isArray(jsonData[key]) && jsonData[key].length > 0
            );
            if (arrayKeys.length > 0) tableData = jsonData[arrayKeys[0]];
          }

          if (!tableData || tableData.length === 0) {
            throw new Error(
              "No valid data found in API response for CSV conversion"
            );
          }

          const allKeys = [
            ...new Set(tableData.flatMap((item) => Object.keys(item))),
          ];
          const csvContent = [
            allKeys.join(","),
            ...tableData.map((item) =>
              allKeys
                .map((header) => {
                  const value = item[header] || "";
                  return `"${String(value).replace(/"/g, '""')}"`;
                })
                .join(",")
            ),
          ].join("\n");

          const blob = new Blob([csvContent], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `${filename}.csv`;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 100);

          return { success: true };
        } else {
          const csvText = await response.text();
          const blob = new Blob([csvText], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `${filename}.csv`;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 100);
          return { success: true };
        }
      }

      return { success: false, error: "Unsupported format" };
    } catch (error) {
      console.error("Download Error Details:", error);
      return {
        success: false,
        error: error.message || "Download failed due to network error",
      };
    }
  };

  // ------------- Table renderer (unchanged logic, but using messages) -------------
  const renderTableFromJSON = (data, messageId) => {
    try {
      if (!data) return null;

      let tableData = null;
      let tableName = "";

      if (Array.isArray(data)) {
        if (
          data.length > 0 &&
          typeof data[0] === "object" &&
          data[0] !== null
        ) {
          tableData = data;
          tableName = "Results";
        } else {
          tableData = data.map((item, index) => ({
            Index: index + 1,
            Value: item,
          }));
          tableName = "Results";
        }
      } else if (typeof data === "object" && data !== null) {
        if (
          data.message &&
          typeof data.message === "string" &&
          Object.keys(data).length === 1
        ) {
          return null;
        }
        if (
          data.detail &&
          typeof data.detail === "string" &&
          Object.keys(data).length === 1
        ) {
          return null;
        }

        const arrayKeys = Object.keys(data).filter(
          (key) => Array.isArray(data[key]) && data[key].length > 0
        );

        if (arrayKeys.length > 0) {
          const firstArrayKey = arrayKeys[0];
          const firstArray = data[firstArrayKey];

          if (
            firstArray[0] &&
            typeof firstArray[0] === "object" &&
            firstArray[0] !== null
          ) {
            tableData = firstArray;
            tableName = firstArrayKey
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase());
          } else {
            tableData = firstArray.map((item, index) => ({
              Index: index + 1,
              [firstArrayKey
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase())]: item,
            }));
            tableName = firstArrayKey
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase());
          }
        } else {
          const objectKeys = Object.keys(data);
          if (objectKeys.length > 0) {
            tableData = objectKeys.map((key) => ({
              Property: key
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase()),
              Value:
                typeof data[key] === "object" && data[key] !== null
                  ? JSON.stringify(data[key])
                  : String(data[key] || ""),
            }));
            tableName = "Details";
          }
        }
      }

      if (!tableData || !Array.isArray(tableData) || tableData.length === 0)
        return null;

      const validTableData = tableData.filter(
        (item) => item && typeof item === "object" && item !== null
      );
      if (validTableData.length === 0) return null;

      const allKeys = [
        ...new Set(validTableData.flatMap((item) => Object.keys(item))),
      ];

      return (
        <div
          className={`w-full mt-3 ${
            allKeys.length > 7 ? "overflow-x-auto" : ""
          }`}
        >
          {tableName && (
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              {tableName}
            </h4>
          )}
          <table
            className={`min-w-full bg-white border border-gray-200 rounded-lg text-xs ${
              allKeys.length > 7 ? "table-fixed" : ""
            }`}
          >
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap w-12">
                  <button
                    onClick={() =>
                      handleSelectAll(messageId, validTableData.length)
                    }
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                    title="Select all"
                  >
                    {Array.from({ length: validTableData.length }).every(
                      (_, i) => selectedRows[`${messageId}-${i}`]
                    ) ? (
                      <CheckSquare className="w-4 h-4 text-blue-600" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                </th>
                {allKeys.map((key, index) => (
                  <th
                    key={index}
                    className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap"
                  >
                    {(() => {
                      const headerMappings = {
                        SAPTableName: "SAP Table Name",
                        SAPColumnName: "SAP Column Name",
                        AXTableName: "AX Table Name",
                        AXColumnName: "AX Column Name",
                        ObjectName: "Object Name",
                        Description: "Description",
                        Mandatory: "Mandatory",
                      };
                      if (headerMappings[key]) return headerMappings[key];
                      return key.replace(/([a-z])([A-Z])/g, "$1 $2");
                    })()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {validTableData.map((item, rowIndex) => {
                const rowKey = `${messageId}-${rowIndex}`;
                const isSelected = selectedRows[rowKey];
                return (
                  <tr
                    key={rowIndex}
                    className={`hover:bg-gray-50 ${
                      isSelected ? "bg-blue-50" : ""
                    }`}
                  >
                    <td className="px-3 py-2 text-xs border-b w-12">
                      <button
                        onClick={() => handleRowSelect(messageId, rowIndex)}
                        className="text-gray-600 hover:text-blue-600 transition-colors"
                      >
                        {isSelected ? (
                          <CheckSquare className="w-4 h-4 text-blue-600" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                    {allKeys.map((key, colIndex) => {
                      const value = item[key] || "";
                      const displayValue =
                        typeof value === "object" && value !== null
                          ? JSON.stringify(value)
                          : String(value);

                      return (
                        <td
                          key={colIndex}
                          className={`px-2 py-2 text-xs text-gray-900 border-b whitespace-nowrap overflow-hidden text-ellipsis ${
                            allKeys.length > 7 ? "min-w-32" : "max-w-xs"
                          }`}
                          title={displayValue}
                        >
                          {key.toLowerCase().includes("status") ? (
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                displayValue.includes("✅") ||
                                displayValue.toLowerCase().includes("mapped")
                                  ? "bg-green-100 text-green-800"
                                  : displayValue.includes("❌") ||
                                    displayValue
                                      .toLowerCase()
                                      .includes("not mapped")
                                  ? "bg-red-100 text-red-800"
                                  : displayValue.includes("⚠️") ||
                                    displayValue
                                      .toLowerCase()
                                      .includes("partial")
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {displayValue}
                            </span>
                          ) : key.toLowerCase().includes("mandatory") ? (
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                displayValue.toLowerCase() === "yes" ||
                                displayValue.toLowerCase() === "true"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {displayValue}
                            </span>
                          ) : (
                            <span
                              className={
                                key.toLowerCase().includes("column") ||
                                key.toLowerCase().includes("table") ||
                                key.toLowerCase().includes("name")
                                  ? "font-medium"
                                  : ""
                              }
                            >
                              {displayValue}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    } catch (error) {
      console.error("Error in renderTableFromJSON:", error);
      return (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">
            Error rendering table data: {error.message}
          </p>
        </div>
      );
    }
  };

  // ------------- Sending messages & handling responses -------------
  const sendMessage = async () => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: message,
      sender: "user",
      timestamp: new Date(),
    };

    // // Update chat title on first user message (keeps consistent with earlier behavior)
    // if (chatTitle === "Falcon AI Chat") {
    //   const snippet = message.slice(0, 30) + (message.length > 30 ? "..." : "");
    //   setChatTitle(snippet);
    // }

    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setIsTyping(true);

    try {
      const apiResult = await callAgentAPI(userMessage.text);

      if (apiResult.success) {
        let aiResponseData = null;
        let aiResponseText = "";
        let hasDownloadableData = false;

        if (typeof apiResult.data === "string") {
          try {
            const parsedData = JSON.parse(apiResult.data);
            if (Array.isArray(parsedData) && parsedData.length > 0) {
              aiResponseData = parsedData;
              aiResponseText = "Here are the results:";
              hasDownloadableData = true;
            } else if (parsedData && typeof parsedData === "object") {
              aiResponseData = parsedData;
              aiResponseText =
                parsedData.message ||
                parsedData.detail ||
                "Here are the results:";
              hasDownloadableData = false;
            } else {
              aiResponseText = apiResult.data;
              hasDownloadableData = false;
            }
          } catch (parseError) {
            aiResponseText = apiResult.data;
            hasDownloadableData = false;
          }
        } else if (Array.isArray(apiResult.data)) {
          aiResponseData = apiResult.data;
          aiResponseText = "Here are the results:";
          hasDownloadableData = true;
        } else if (apiResult.data && typeof apiResult.data === "object") {
          if (
            apiResult.data.message &&
            typeof apiResult.data.message === "string" &&
            Object.keys(apiResult.data).length === 1
          ) {
            aiResponseText = apiResult.data.message;
            hasDownloadableData = false;
          } else if (
            apiResult.data.detail &&
            typeof apiResult.data.detail === "string" &&
            Object.keys(apiResult.data).length === 1
          ) {
            aiResponseText = apiResult.data.detail;
            hasDownloadableData = false;
          } else {
            aiResponseData = apiResult.data;
            aiResponseText =
              apiResult.data.message ||
              apiResult.data.detail ||
              "Here are the results:";
            hasDownloadableData = true;
          }
        } else {
          aiResponseText = apiResult.data
            ? String(apiResult.data)
            : "No results found.";
          hasDownloadableData = false;
        }

        const aiResponse = {
          id: Date.now() + 1,
          text: aiResponseText,
          sender: "ai",
          timestamp: new Date(),
          hasDownload: hasDownloadableData,
          originalQuery: userMessage.text,
          data: aiResponseData,
        };

        setMessages((prev) => [...prev, aiResponse]);
      } else {
        const errorResponse = {
          id: Date.now() + 1,
          text: `${apiResult.error}`,
          sender: "ai",
          timestamp: new Date(),
          hasDownload: false,
        };
        setMessages((prev) => [...prev, errorResponse]);
      }
    } catch (error) {
      const errorResponse = {
        id: Date.now() + 1,
        text: `I apologize, but I'm currently unable to process your request due to a technical issue. Please try again later.`,
        sender: "ai",
        timestamp: new Date(),
        hasDownload: false,
      };
      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleDownload = async (msg, format) => {
    if (!msg.originalQuery) return;

    const result = await downloadResponse(msg.originalQuery, format);
    if (!result.success) {
      console.log("API download failed, trying fallback method...");
      if (msg.data) {
        const timestamp = new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/:/g, "-");
        const filename = `mapping_result_${timestamp}`;
        const fallbackResult = generateFileFromDisplayedData(
          msg.data,
          format,
          filename
        );
        if (!fallbackResult.success) {
          toast.error(
            `Download failed: ${result.error}. Fallback also failed: ${fallbackResult.error}`
          );
        }
      } else {
        toast.error(`Download failed: ${result.error}`);
      }
    }
  };
  const spinnerStyle = `
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = spinnerStyle;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // ------------- UI (responsive fullscreen layout) -------------
  return (
    <div className="flex flex-col h-full w-full bg-gray-50">
      {/* Header */}
      {/* <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-white border">
              <img src={logo1} alt="AI Logo" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-lg font-semibold text-gray-800 leading-8">Bluegecko AI</h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <img src={logo} alt="Falcon AI Logo" className="w-8 h-8 rounded-full object-cover cursor-pointer" />
        </div>
      </div> */}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        <div className="max-w-[1100px] mx-auto space-y-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-3 ${
                msg.sender === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {msg.sender === "ai" && (
                <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden">
                  <img
                    src={logo1}
                    alt="AI Logo"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div
                className={`${
                  msg.sender === "user" ? "max-w-3xl" : "max-w-5xl"
                }`}
              >
                <div
                  className={`px-4 py-2 rounded-lg ${
                    msg.sender === "user"
                      ? "bg-blue-500 text-white"
                      : "bg-white border border-gray-200 text-gray-800"
                  }`}
                >
                  <p className="whitespace-pre-wrap">{msg.text}</p>

                  {msg.sender === "ai" &&
                    msg.data &&
                    renderTableFromJSON(msg.data, msg.id)}

                  <span className="text-xs opacity-70 mt-1 block">
                    {msg.timestamp.toLocaleTimeString()}
                  </span>
                </div>

                {msg.sender === "ai" && msg.hasDownload && (
                  <div className="mt-3 space-y-2">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDownload(msg, "csv")}
                        className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                      >
                        <Download className="w-3 h-3" />
                        CSV
                      </button>
                    </div>


{/* APPROVE BUTTON */}
{(() => {
                                      let actualTableData = null;
                                     
                                      if (Array.isArray(msg.data)) {
                                        actualTableData = msg.data;
                                      } else if (msg.data && typeof msg.data === "object") {
                                        const arrayKeys = Object.keys(msg.data).filter(
                                          (key) => Array.isArray(msg.data[key]) && msg.data[key].length > 0
                                        );
                                        if (arrayKeys.length > 0) {
                                          actualTableData = msg.data[arrayKeys[0]];
                                        }
                                      }
                   
                                      if (!actualTableData || !Array.isArray(actualTableData) || actualTableData.length === 0) {
                                        return null;
                                      }
                   
                                      const selectedIndices = [];
                                      actualTableData.forEach((_, index) => {
                                        if (selectedRows[`${msg.id}-${index}`]) {
                                          selectedIndices.push(index);
                                        }
                                      });
                   
                                      if (selectedIndices.length === 0) {
                                        return null;
                                      }
                                      const isApproving = approvingMessageId === msg.id && approvalInProgress;
                   
                                      return (
                                        <button
                                          onClick={() => {
                                            const selectedRowsData = selectedIndices.map(i => actualTableData[i]);
                                            handleApproveSelected(msg.id, selectedRowsData);
                                          }}
                                          disabled={isApproving}
                                          className={`flex items-center gap-1 px-3 py-1 text-xs text-white rounded-md transition-all ${
                                            isApproving
                                              ? 'bg-green-400 cursor-not-allowed'
                                              : 'bg-green-500 hover:bg-green-600'
                                          }`}
                                          title={isApproving ? "processing..": `Approve ${selectedIndices.length} selected mappings`}
                                        >
                                          {isApproving ? (
                                            <>
                                           
                                              <span>Approving...</span>
                                            </>
                                          ) : (
                                            <>
                                              <Check className="w-3 h-3" />
                                              <span>Approve Selected ({selectedIndices.length})</span>
                                            </>
                                          )}
                                        </button>
                                      );
                                    })()}
                                  </div>
                                )}
                              </div>
                    
              {msg.sender === "user" && (
                <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          ))}

          {isTyping && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden">
                <img
                  src={logo1}
                  alt="AI Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-white border border-gray-200 px-4 py-2 rounded-lg">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area (sticky) */}
      <div className="bg-white border-t border-gray-200 px-4 py-3 flex-shrink-0">
        <div className="max-w-4xl mx-auto">
          <div className="relative flex items-center">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="w-full pr-12 pl-4 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="1"
              style={{ minHeight: "48px", maxHeight: "120px" }}
            />
            <button
              onClick={sendMessage}
              disabled={!message.trim() || isTyping}
              className={`absolute right-3 p-2 rounded-md transition-colors ${
                message.trim() && !isTyping
                  ? "text-blue-500 hover:text-blue-600"
                  : "text-gray-400 cursor-not-allowed"
              }`}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-gray-500 text-center mt-2">
            Press Enter to send, Shift + Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
};

export default FalconAIChatbot;
